import React, { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCopy,
  FiCheck,
  FiInstagram,
  FiYoutube,
  FiLock,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { REACT_APP_API_PORT } from "../Api";
import { getDecryptedData } from "../config/crypto";
import Loader from "../components/Loader";

const AIPromptDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [copied, setCopied] = useState(false);

  const [unlockName, setUnlockName] = useState("");
  const [unlockEmail, setUnlockEmail] = useState("");
  const [unlockMobile, setUnlockMobile] = useState("");

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");

  const [showMoreResources, setShowMoreResources] = useState(false);
  const [isSavingMobile, setIsSavingMobile] = useState(false);

  const [hasSavedUser, setHasSavedUser] = useState(false);

 

  // =========================
  // SCROLL TOP
  // =========================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const UNLOCK_CACHE_KEY = "instagram_prompt_user";

useEffect(() => {
  const savedUser = localStorage.getItem(UNLOCK_CACHE_KEY);

  if (savedUser) {
    try {
      const parsedUser = JSON.parse(savedUser);

      if (parsedUser?.email) {
        setUnlockName(parsedUser.name || "");
        setUnlockEmail(parsedUser.email || "");
        setUnlockMobile(parsedUser.mobile || "");
        setHasSavedUser(true);
        setIsUnlocked(true);
      }
    } catch (error) {
      localStorage.removeItem(UNLOCK_CACHE_KEY);
      setHasSavedUser(false);
    }
  }
}, []);



const handleUnlockPrompt = async (e) => {
  e.preventDefault();

  const name = unlockName.trim();
  const email = unlockEmail.trim().toLowerCase();

  if (!name) {
    setUnlockError("Please enter your name");
    return;
  }

  if (!email) {
    setUnlockError("Please enter your email");
    return;
  }

  try {
    setIsUnlocking(true);
    setUnlockError("");

    const res = await axios.post(
      `${REACT_APP_API_PORT}user/homepage/insta_users`,
      {
        name,
        email,
      }
    );

    if (res.data?.status) {
      localStorage.setItem(
        UNLOCK_CACHE_KEY,
        JSON.stringify({
          name,
          email,
        })
      );

      setIsUnlocked(true);
    } else {
      setUnlockError(
        res.data?.message || "Something went wrong. Please try again."
      );
    }
  } catch (error) {
    console.error("Unlock error:", error);

    setUnlockError(
      error?.response?.data?.message ||
        "Something went wrong. Please try again."
    );
  } finally {
    setIsUnlocking(false);
  }
};

const handleMoreResources = async (e) => {
  e.preventDefault();

  const name = unlockName.trim();
  const email = unlockEmail.trim().toLowerCase();
  const mobile = unlockMobile.trim();

  if (!name) {
    setUnlockError("Please enter your name");
    return;
  }

  if (!email) {
    setUnlockError("Please enter your email");
    return;
  }

  if (!mobile) {
    setUnlockError("Please enter your mobile number");
    return;
  }

  try {
    setIsSavingMobile(true);
    setUnlockError("");

    const res = await axios.post(
      `${REACT_APP_API_PORT}user/homepage/insta_users`,
      {
        name,
        email,
        mobile,
      }
    );

   if (res.data?.status) {
  localStorage.setItem(
    UNLOCK_CACHE_KEY,
    JSON.stringify({
      name,
      email,
      mobile,
    })
  );

  window.open(
    "https://nextfutureai.com/all-guide",
    "_blank",
    "noopener,noreferrer"
  );
} else {
      setUnlockError(
        res.data?.message || "Something went wrong. Please try again."
      );
    }
  } catch (error) {
    console.error("Mobile save error:", error);

    setUnlockError(
      error?.response?.data?.message ||
        "Something went wrong. Please try again."
    );
  } finally {
    setIsSavingMobile(false);
  }
};

  // =========================
  // GET DETAIL DATA
  // =========================

  const getDetailData = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        `${REACT_APP_API_PORT}user/homepage/instagram_prompt_detail/${id}`
      );

      if (res.data?.status) {
        setData(res.data?.data || null);
      } else {
        setData(null);
      }
    } catch (error) {
      console.error("Detail API Error:", error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

 

  // =========================
  // API CALL
  // =========================

  useEffect(() => {
    if (id) {
      getDetailData();
    }
  }, [id]);

  // =========================
  // CLEAN HTML TEXT
  // =========================

  const plainDescription = useMemo(() => {
  if (!data?.short_description) return "";

  const div = document.createElement("div");
  div.innerHTML = data.short_description;

  const blockTags = [
    "P",
    "DIV",
    "BR",
    "LI",
    "UL",
    "OL",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
  ];

  const getText = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    if (node.tagName === "BR") {
      return "\n";
    }

    let text = "";

    node.childNodes.forEach((child) => {
      text += getText(child);
    });

    if (blockTags.includes(node.tagName)) {
      text += "\n";
    }

    return text;
  };

  return getText(div)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}, [data?.short_description]);

  // =========================
  // COPY PROMPT
  // =========================

  const handleCopy = async () => {
    if (!plainDescription) return;

    try {
      await navigator.clipboard.writeText(plainDescription);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Copy error:", error);
    }
  };

  // =========================
  // TAGS
  // =========================

  const formattedTags = useMemo(() => {
    if (!data?.tags) return [];

    const tagsArray = Array.isArray(data.tags)
      ? data.tags
      : [data.tags];

    return [
      ...new Set(
        tagsArray
          .flatMap((tag) => String(tag).split(","))
          .map((tag) => tag.trim())
          .filter(Boolean)
      ),
    ];
  }, [data?.tags]);

  // =========================
  // GET FIRST TAG
  // =========================

  const getFirstTag = (item) => {
    if (!item?.tags) return "";

    const tags = Array.isArray(item.tags)
      ? item.tags
      : [item.tags];

    const firstTag = tags
      .flatMap((tag) => String(tag).split(","))
      .map((tag) => tag.trim())
      .filter(Boolean)[0];

    return firstTag || "";
  };

  // =========================
// INSTAGRAM EMBED URL
// =========================


useEffect(() => {
  if (!data?.instagram_link) return;

  const processInstagram = () => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  };

  const existingScript = document.querySelector(
    'script[src="https://www.instagram.com/embed.js"]'
  );

  if (existingScript) {
    processInstagram();
    return;
  }

  const script = document.createElement("script");
  script.src = "https://www.instagram.com/embed.js";
  script.async = true;
  script.onload = processInstagram;

  document.body.appendChild(script);
}, [data?.instagram_link]); 

  const openResource = (url) => {
  const savedUser = localStorage.getItem(UNLOCK_CACHE_KEY);

  if (!savedUser) {
    // Direct visitor → Name + Email + Mobile form
    navigate("/resource-access");
    return;
  }

  window.location.href = url;
};

const openChatGPTGuide = () => {
  window.open(
    "https://nextfutureai.com/chatgpt-guide",
    "_blank",
    "noopener,noreferrer"
  );
};

const openClaudeGuide = () => {
  window.open(
    "https://nextfutureai.com/claude-guide",
    "_blank",
    "noopener,noreferrer"
  );
};

  // =========================
  // LOADING
  // =========================

  if (isLoading) {
    return <Loader isLoading={true} />;
  }

  // =========================
  // NOT FOUND
  // =========================

  if (!data) {
    return (
      <>
        <header className="sticky top-0 z-50 border-b border-white/10 bg-[#111111]/95 backdrop-blur">
          <div className="mx-auto flex h-[64px] max-w-[1180px] items-center justify-between px-5 sm:px-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
            >
              <FiArrowLeft />
              Back
            </button>
          </div>
        </header>

        <main className="flex min-h-screen items-center justify-center bg-[#111111] px-5 text-white">
          <div className="text-center">
            <h1 className="text-2xl font-semibold">
              Prompt not found
            </h1>

            <button
              onClick={() => navigate("/ai-prompts")}
              className="mt-5 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-black"
            >
              View all prompts
            </button>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={false} />

      {/* ================= HEADER ================= */}

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#111111]/95 backdrop-blur">
        <div className="mx-auto flex h-[64px] max-w-[1180px] items-center justify-between px-5 sm:px-6">

          <button
            type="button"
            onClick={() => navigate("/ai-prompts")}
            className="flex items-center gap-3"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#202020] text-sm font-bold text-white">
              S
            </div>

            <span className="text-[17px] font-semibold tracking-[-0.02em] text-white">
              Satyam AI
            </span>
          </button>

          <a
            href="https://www.instagram.com/nextfutureai/"
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-white/70 transition hover:text-white"
          >
            Instagram
          </a>

        </div>
      </header>

      {/* ================= MAIN ================= */}

      <main className="min-h-screen bg-[#111111] text-white">

        <div className="mx-auto max-w-[1180px] px-5 pb-20 pt-8 sm:px-6 sm:pt-12">

          {/* BACK */}

          <button
            type="button"
            onClick={() => navigate("/ai-prompts")}
            className="mb-8 flex items-center gap-2 text-[14px] text-white/60 transition hover:text-white"
          >
            <FiArrowLeft className="text-base" />
            All prompts
          </button>


          {/* ================= MAIN GRID ================= */}

          <section className="grid gap-10 lg:grid-cols-[420px_minmax(0,1fr)] lg:gap-14">

            {/* ================= LEFT INSTAGRAM ================= */}

            <div className="w-full">
            {data?.instagram_link ? (
  <div className="overflow-hidden rounded-[22px] border border-white/10 bg-[#181818] shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
    <blockquote
  className="instagram-media"
  data-instgrm-permalink={data.instagram_link}
  data-instgrm-version="14"
  style={{
    background: "#fff",
    border: 0,
    margin: "0 auto",
    maxWidth: "540px",
    minWidth: "326px",
    width: "100%",
  }}
>
</blockquote>
  </div>
) : (
                <div className="flex min-h-[500px] items-center justify-center rounded-[22px] border border-white/10 bg-[#181818] p-8 text-center">
                <div>
                    <FiInstagram className="mx-auto text-4xl text-white/30" />

                    <p className="mt-4 text-sm text-white/50">
                    Instagram preview not available
                    </p>
                </div>
                </div>
            )}
            </div>


            {/* ================= RIGHT CONTENT ================= */}

            <div className="min-w-0">

              {/* TAGS */}

              <div className="flex flex-wrap items-center gap-2">

                {formattedTags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-[#252525] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-white"
                  >
                    {tag}
                  </span>
                ))}

                {data?.prompt_type && (
                  <span className="rounded-full bg-white/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-white/50">
                    {data.prompt_type}
                  </span>
                )}

              </div>


              {/* TITLE */}

              <h1 className="mt-5 max-w-[700px] font-serif text-[30px] font-semibold leading-[1.1] tracking-[-0.035em] text-white sm:text-[38px]">
  {data?.title}
</h1>



              {/* ================= PROMPT ACCESS ================= */}

{isUnlocked ? (
  <>
    {/* UNLOCKED STATUS */}

    <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#7ac5a8]/20 bg-[#7ac5a8]/10 px-4 py-2 text-[12px] font-semibold text-[#9be1c3]">
      <span className="flex h-4 w-4 items-center justify-center rounded-full border border-[#9be1c3]/60">
        ✓
      </span>

      Prompt unlocked
    </div>


    {/* ================= PRIMARY PROMPT ================= */}

    {data?.short_description && (
      <section className="mt-10">

        <div className="mb-4 flex items-center justify-between">

          <h2 className="text-[12px] font-bold uppercase tracking-[0.12em] text-white/50">
            Primary Prompt
          </h2>

          <span className="text-[12px] text-white/40">
            Ready to use
          </span>

        </div>


        <div className="overflow-hidden rounded-[18px] border border-white/10 bg-[#181818] shadow-[0_15px_40px_rgba(0,0,0,0.18)]">

          {/* EDITOR HEADER */}

          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">

            <div className="flex items-center gap-2">

              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />

              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />

              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />

              <span className="ml-3 text-[11px] text-white/30">
                prompt.md
              </span>

            </div>


            <span className="rounded-md bg-white/[0.05] px-2.5 py-1 text-[10px] text-white/40">
              {data?.prompt_type || "Prompt"}
            </span>

          </div>


          {/* PROMPT CONTENT */}

          <div className="max-h-[350px] overflow-y-auto px-5 py-6">
  <div
    className="
      prompt-content text-[15px] leading-[1.9] text-white
      [&_*]:!text-white
    "
    dangerouslySetInnerHTML={{
      __html: data?.short_description || "",
    }}
  />
</div>


          {/* COPY BUTTON */}

          <div className="border-t border-white/10 p-3">

            <button
              type="button"
              onClick={handleCopy}
              className={`flex w-full items-center justify-center gap-2 rounded-[12px] px-5 py-3 text-[14px] font-semibold transition ${
                copied
                  ? "bg-[#2d7d5e] text-white"
                  : "bg-white text-black hover:bg-white/90"
              }`}
            >
              {copied ? (
                <>
                  <FiCheck />
                  Copied
                </>
              ) : (
                <>
                  <FiCopy />
                  Copy prompt
                </>
              )}
            </button>

          </div>

        </div>

      </section>
    )}
  </>
) : (
  <>
    {/* ================= LOCKED PROMPT PREVIEW ================= */}

    <section className="mt-8 overflow-hidden rounded-[20px] border border-white/10 bg-[#181818]">

      {/* Fake editor */}

      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/60" />

        <span className="ml-3 text-[11px] text-white/20">
          prompt.md
        </span>
      </div>


      <div className="relative flex min-h-[250px] items-center justify-center overflow-hidden px-6 py-12">

        {/* Blurred fake content */}

        <div className="absolute inset-0 p-6 opacity-20 blur-[5px]">
          <div className="space-y-4">
            <div className="h-4 w-full rounded bg-white/30" />
            <div className="h-4 w-[90%] rounded bg-white/30" />
            <div className="h-4 w-[95%] rounded bg-white/30" />
            <div className="h-4 w-[75%] rounded bg-white/30" />
            <div className="h-4 w-[88%] rounded bg-white/30" />
          </div>
        </div>


        {/* Lock */}

        <div className="relative z-10 text-center">

          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] border border-white/10 bg-[#222] shadow-[0_10px_30px_rgba(0,0,0,0.3)]">
            <FiLock className="text-2xl text-white/70" />
          </div>

          <h3 className="mt-5 text-[16px] font-semibold text-white">
            Prompt locked
          </h3>

          <p className="mt-2 text-[13px] text-white/45">
            Enter your details below to unlock this prompt.
          </p>

        </div>

      </div>

    </section>


    {/* ================= UNLOCK FORM ================= */}

    <section className="mt-6 rounded-[20px] border border-white/10 bg-[#181818] p-5 sm:p-7">

      <div className="text-center">

        <h2 className="text-[20px] font-semibold text-white">
          Unlock this prompt
        </h2>

        <p className="mt-2 text-[13px] text-white/50">
          Enter your email to view and copy the full prompt.
        </p>

      </div>


      <form
        onSubmit={handleUnlockPrompt}
        className="mt-6 space-y-3"
      >

        {/* EMAIL */}

       


        {/* NAME */}

        <input
          type="text"
          value={unlockName}
          onChange={(e) => setUnlockName(e.target.value)}
          placeholder="First name"
          className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-4 text-[14px] text-white outline-none placeholder:text-white/30 focus:border-white/25"
        />

         <input
          type="email"
          value={unlockEmail}
          onChange={(e) => {
            setUnlockEmail(e.target.value);
            setUnlockError("");
          }}
          placeholder="you@gmail.com"
          className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-4 text-[14px] text-white outline-none placeholder:text-white/30 focus:border-white/25"
        />


        {/* ERROR */}

        {unlockError && (
          <p className="text-center text-[12px] text-red-400">
            {unlockError}
          </p>
        )}


        {/* SUBMIT */}

        <button
          type="submit"
          disabled={isUnlocking}
          className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-white px-5 py-4 text-[14px] font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUnlocking ? (
            "Unlocking..."
          ) : (
            <>
              Unlock prompt
              <FiLock />
            </>
          )}
        </button>

      </form>


      <p className="mt-4 text-center text-[11px] text-white/30">
        No spam. Your details stay private.
      </p>

    </section>
  </>
)}


              {/* ================= HOW TO USE ================= */}

              <section className="mt-7 rounded-[18px] border border-white/10 bg-[#181818] p-6">

                <h2 className="text-[12px] font-bold uppercase tracking-[0.12em] text-white/50">
                  How to use
                </h2>


                <div className="mt-6 space-y-5">

                  <div className="flex gap-4">

                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-black">
                      1
                    </div>

                    <div>
                      <h3 className="text-[14px] font-semibold text-white">
                        Copy the prompt
                      </h3>

                      <p className="mt-1 text-[13px] text-white/50">
                        Click the copy button above.
                      </p>
                    </div>

                  </div>


                  <div className="flex gap-4">

                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-xs font-bold text-white/60">
                      2
                    </div>

                    <div>
                      <h3 className="text-[14px] font-semibold text-white">
                        Open the AI tool
                      </h3>

                      <p className="mt-1 text-[13px] text-white/50">
                        Open the tool mentioned in this prompt.
                      </p>
                    </div>

                  </div>


                  <div className="flex gap-4">

                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-xs font-bold text-white/60">
                      3
                    </div>

                    <div>
                      <h3 className="text-[14px] font-semibold text-white">
                        Paste and generate
                      </h3>

                      <p className="mt-1 text-[13px] text-white/50">
                        Add your image or details if required and generate.
                      </p>
                    </div>

                  </div>

                </div>

              </section>


              {/* ================= ACTION BUTTONS ================= */}

              <div className="mt-7 grid gap-3 sm:grid-cols-2">

              {data?.instagram_link && (
                <a
                  href={data.instagram_link}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 rounded-[14px] bg-[#e44b32] px-5 py-3.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  <FiInstagram />
                  View on Instagram
                </a>
              )}

              <button
                type="button"
                onClick={() => navigate("/ai-prompts")}
                className="flex items-center justify-center gap-2 rounded-[14px] bg-[#168cff] px-5 py-3.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                All Prompts
              </button>

            </div>

            </div>

          </section>

                {/* ================= FREE AI RESOURCES ================= */}

<section className="mt-8 space-y-4">

  {/* CHATGPT */}
  <button
    type="button"
    onClick={openChatGPTGuide}
    className="group relative w-full rounded-[18px] border border-white/20 bg-[#1b1b1b] px-4 py-3 text-left shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition duration-200 hover:-translate-y-[2px] hover:border-white/35 hover:bg-[#202020]"
  >
    <div className="flex items-center gap-3">

      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[11px] bg-black">
        <img
          src="/icons/chatgpt.png"
          alt="ChatGPT"
          className="h-full w-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">
          Free Resource
        </div>

        <h2 className="text-[16px] font-semibold tracking-[-0.02em] text-white">
          ChatGPT 600+ Shortcuts & Hacks
        </h2>

        <p className="mt-1 text-[12px] leading-4 text-white/50">
          Explore 600+ useful ChatGPT codes, shortcuts and prompt commands.
        </p>
      </div>

      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/50 transition group-hover:border-white/20 group-hover:text-white">
        <FiChevronRight className="text-lg" />
      </div>

    </div>

    <div className="mt-3 border-t border-white/[0.08] pt-2.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/35">
      Open ChatGPT Guide
    </div>
  </button>


  {/* CLAUDE */}
  <button
    type="button"
    onClick={openClaudeGuide}
    className="group relative w-full rounded-[18px] border border-white/20 bg-[#1b1b1b] px-4 py-3 text-left shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition duration-200 hover:-translate-y-[2px] hover:border-white/35 hover:bg-[#202020]"
  >
    <div className="flex items-center gap-3">

      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[11px] bg-black">
        <img
          src="/icons/Claude.png"
          alt="Claude"
          className="h-full w-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">
          Free Resource
        </div>

        <h2 className="text-[16px] font-semibold tracking-[-0.02em] text-white">
          Claude 50+ Shortcuts & Hacks
        </h2>

        <p className="mt-1 text-[12px] leading-4 text-white/50">
          Explore useful Claude shortcuts, commands and AI hacks.
        </p>
      </div>

      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/50 transition group-hover:border-white/20 group-hover:text-white">
        <FiChevronRight className="text-lg" />
      </div>

    </div>

    <div className="mt-3 border-t border-white/[0.08] pt-2.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/35">
      Open Claude Guide
    </div>
  </button>


  {/* GOOGLE FLOW - LOCKED */}
  <div className="relative overflow-hidden rounded-[18px] border border-white/10 bg-[#181818]">

    <div className="flex items-center gap-3 px-4 py-4 opacity-50">

      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[11px] bg-black">
        <img
          src="/icons/googleFlowWhite.png"
          alt="Google Flow"
          className="h-full w-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">
          Locked Resource
        </div>

        <h2 className="text-[16px] font-semibold text-white">
          Google Flow 200+ Shortcuts & Hacks
        </h2>

        <p className="mt-1 text-[12px] leading-4 text-white/50">
          Explore Google Flow tips, prompts and useful AI techniques.
        </p>
      </div>

      <FiLock className="shrink-0 text-lg text-white/50" />

    </div>

  </div>


  {/* MORE RESOURCES */}
  <button
    type="button"
    onClick={() => {
      setShowMoreResources(true);
      setUnlockError("");
    }}
    className="mt-2 flex w-full items-center justify-center gap-2 rounded-[14px] bg-white px-5 py-4 text-[14px] font-semibold text-black transition hover:bg-white/90"
  >
    Get More Free AI Resources
    <FiChevronRight />
  </button>

</section>

{showMoreResources && (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-5 backdrop-blur-sm">

    <div className="w-full max-w-[440px] rounded-[22px] border border-white/10 bg-[#181818] p-6 shadow-[0_25px_80px_rgba(0,0,0,0.5)]">

      <div className="text-center">

        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[16px] bg-white text-black">
          🚀
        </div>

        <h2 className="mt-5 text-[20px] font-semibold text-white">
          Unlock More Free Resources
        </h2>

        <p className="mt-2 text-[13px] leading-5 text-white/50">
          Enter your WhatsApp number to access our complete AI resource library.
        </p>

      </div>

      <form onSubmit={handleMoreResources} className="mt-6 space-y-3">
   {!hasSavedUser && (
  <>
    <input
      type="text"
      value={unlockName}
      onChange={(e) => {
        setUnlockName(e.target.value);
        setUnlockError("");
      }}
      placeholder="Your name"
      className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-4 text-white outline-none"
    />

    <input
      type="email"
      value={unlockEmail}
      onChange={(e) => {
        setUnlockEmail(e.target.value);
        setUnlockError("");
      }}
      placeholder="Your email"
      className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-4 text-white outline-none"
    />
  </>
)}
  <input
    type="tel"
    value={unlockMobile}
    onChange={(e) => {
      setUnlockMobile(e.target.value);
      setUnlockError("");
    }}
    placeholder="Mobile number"
    className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-4 text-white outline-none"
  />

  {unlockError && (
    <p className="text-center text-[12px] text-red-400">
      {unlockError}
    </p>
  )}

  <button
    type="submit"
    disabled={isSavingMobile}
    className="w-full rounded-[14px] bg-white px-5 py-4 text-[14px] font-semibold text-black disabled:opacity-60"
  >
    {isSavingMobile ? "Unlocking..." : "Unlock Resources"}
  </button>

</form>

<button
  type="button"
  onClick={() => {
    setShowMoreResources(false);
    setUnlockError("");
  }}
  className="mt-3 w-full rounded-[14px] border border-white/10 bg-white/[0.04] px-5 py-3 text-[13px] font-medium text-white/50 transition hover:bg-white/[0.08] hover:text-white"
>
  Cancel
</button>

    </div>

  </div>
)}
         



        </div>

      </main>
    </>
  );
};

export default AIPromptDetailPage;